import { cva } from "class-variance-authority";
import { ChevronDownIcon, XIcon } from "lucide-react";
import {
  type KeyboardEvent,
  type MouseEvent,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useTranslation } from "../../i18n";
import { cn, mergeRefs } from "../../utils";
import { IconButton } from "../Button/IconButton";
import { type ButtonSize } from "../Button/types";
import { useDropdownContext } from "../Dropdown";
import { Spinner } from "../Spinner";
import { type SpinnerColor, type SpinnerSize } from "../Spinner/types";
import { Tag } from "../Tag";
import { TruncatedContent } from "../TruncatedContent";
import { useSelectContext } from "./context";
import { SelectSize, SelectTriggerProps } from "./types";

const selectTriggerVariants = cva(
  "group/select box-border w-full relative flex items-center border overflow-hidden border-neutral-100 hover:border-neutral-200 hover:outline hover:outline-[2px] hover:outline-neutral-200 focus:outline focus:outline-[2px] focus:outline-primary-500 focus:hover:outline-primary-500 cursor-pointer",
  {
    variants: {
      size: {
        sm: "min-h-8 h-8 rounded-xl text-sm",
        md: "min-h-10 h-10 rounded-xl text-sm",
        lg: "min-h-12 h-12 rounded-xl text-md",
      },
      invalid: {
        true: "outline outline-[2px] border-danger-500 outline-danger-500 hover:border-danger-500 hover:outline-danger-500 focus:border-danger-500 focus:outline-danger-500 focus:hover:border-danger-500 focus:hover:outline-danger-500",
      },
      disabled: {
        true: "pointer-events-none text-neutral-200 bg-neutral-100 border-neutral-200",
        false: "bg-white",
      },
      open: {
        true: "outline outline-[2px] outline-primary-500 hover:outline-primary-500",
      },
    },
    defaultVariants: {
      size: "md",
    },
  },
);

const accessoryBoxVariants = cva("flex items-center justify-center", {
  variants: {
    size: {
      sm: "w-6 h-6",
      md: "w-8 h-8",
      lg: "w-10 h-10",
    },
  },
});

export const SelectTrigger = (props: SelectTriggerProps = {}) => {
  const {
    size = "md",
    className,
    invalid,
    disabled,
    loading,
    placeholder,
    displayValue,
    showClearButton,
    onClear,
    onRenderAnchor,
    ariaLabel,
    ariaLabelledby,
    ariaDescribedby,
    contentId,
    multiple,
    value,
    options,
    onSelect,
    ...contextProps
  } = useSelectContext();
  const { t } = useTranslation();
  const { open } = useDropdownContext();
  const triggerRef = useRef<HTMLDivElement>(null);
  const shouldDisplayTags = multiple && contextProps.displayMode === "tags";

  const triggerClass = onRenderAnchor
    ? className
    : cn(
        selectTriggerVariants({
          size,
          invalid,
          disabled: !!disabled,
          open,
          className,
        }),
        shouldDisplayTags && "h-auto",
      );

  const buttonSizeMap: Record<SelectSize, ButtonSize> = {
    sm: "xs",
    md: "sm",
    lg: "md",
  };

  const spinnerSizeMap: Record<SelectSize, SpinnerSize> = {
    sm: "2xs",
    md: "xs",
    lg: "xs",
  };

  const spinnerColor: SpinnerColor = "primary";
  const spinnerSize: SpinnerSize = spinnerSizeMap[size];
  const accessoryBoxClass = accessoryBoxVariants({ size });

  const selectedOptions = useMemo(() => {
    if (!shouldDisplayTags || !value) {
      return [];
    }

    const valueArray = Array.isArray(value) ? value : [value];
    return options.filter((opt) => valueArray.includes(opt.id));
  }, [shouldDisplayTags, value, options]);

  const handleClearClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      onClear?.();
      triggerRef.current?.focus();
    },
    [onClear],
  );

  const handleClearMouseDown = useCallback((e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
  }, []);

  const handleClearKeyDown = useCallback((e: KeyboardEvent) => {
    e.stopPropagation();
  }, []);

  const dataAttributes = Object.fromEntries(
    Object.entries(contextProps).filter(([key]) => key.startsWith("data-")),
  );

  return (
    <div
      {...props}
      {...dataAttributes}
      ref={mergeRefs(triggerRef, props.ref)}
      className={triggerClass}
      style={{ outlineOffset: "-1px" }}
      tabIndex={onRenderAnchor || disabled ? -1 : 0}
      role="button"
      aria-haspopup="listbox"
      aria-expanded={open}
      aria-busy={loading ? "true" : undefined}
      aria-disabled={disabled}
      aria-label={!ariaLabelledby ? ariaLabel : undefined}
      aria-labelledby={ariaLabelledby}
      aria-describedby={ariaDescribedby}
      aria-controls={contentId}
    >
      {onRenderAnchor ? (
        onRenderAnchor(open)
      ) : (
        <>
          <div
            className={cn(
              "flex items-center flex-1 overflow-hidden",
              shouldDisplayTags && selectedOptions.length > 0
                ? "ps-1.5"
                : "ps-4",
            )}
          >
            {shouldDisplayTags && selectedOptions.length > 0 ? (
              <div className="flex flex-wrap gap-1 py-1.5 w-full">
                {selectedOptions.map((option) => (
                  <Tag
                    key={option.id}
                    id={option.id}
                    label={option.text}
                    icon={option.icon}
                    className={cn(disabled && "bg-neutral-500/10")}
                    textClassName={cn(
                      "font-normal",
                      disabled && "text-neutral-500/50",
                    )}
                    size={size}
                    onRemove={() => onSelect(option.id)}
                    disabled={disabled}
                  />
                ))}
              </div>
            ) : displayValue ? (
              <TruncatedContent as="span" className="whitespace-nowrap">
                {displayValue}
              </TruncatedContent>
            ) : (
              <span className="text-sm text-neutral-500/50">{placeholder}</span>
            )}
          </div>

          <div
            className={cn(
              "flex items-center pe-[3px]",
              shouldDisplayTags && "self-end pb-[3px]",
            )}
          >
            {showClearButton && (
              <IconButton
                variant="ghost"
                color="neutral"
                size={buttonSizeMap[size]}
                onClick={handleClearClick}
                onKeyDown={handleClearKeyDown}
                onMouseDown={handleClearMouseDown}
                onPointerDown={(e) => e.stopPropagation()}
                content={<XIcon />}
                aria-label={t("select.clearSelection")}
                tabIndex={0}
              />
            )}

            {!disabled && loading ? (
              <div className={accessoryBoxClass}>
                <Spinner color={spinnerColor} size={spinnerSize} />
              </div>
            ) : (
              <IconButton
                variant="ghost"
                color="neutral"
                size={buttonSizeMap[size]}
                tabIndex={-1}
              >
                <ChevronDownIcon
                  className={cn(
                    "transition-transform duration-300",
                    open && "rotate-180",
                  )}
                />
              </IconButton>
            )}
          </div>
        </>
      )}
    </div>
  );
};
